import React, { useState } from 'react';
import './SpotifyAdvancedFilters.css';

// Camelot to Spotify key mapping
const CAMELOT_TO_SPOTIFY = {
  '1A': { key: 8, mode: 0, name: 'Ab minor' },
  '1B': { key: 11, mode: 1, name: 'B major' },
  '2A': { key: 3, mode: 0, name: 'Eb minor' },
  '2B': { key: 6, mode: 1, name: 'Gb major' },
  '3A': { key: 10, mode: 0, name: 'Bb minor' },
  '3B': { key: 1, mode: 1, name: 'Db major' },
  '4A': { key: 5, mode: 0, name: 'F minor' },
  '4B': { key: 8, mode: 1, name: 'Ab major' },
  '5A': { key: 0, mode: 0, name: 'C minor' },
  '5B': { key: 3, mode: 1, name: 'Eb major' },
  '6A': { key: 7, mode: 0, name: 'G minor' },
  '6B': { key: 10, mode: 1, name: 'Bb major' },
  '7A': { key: 2, mode: 0, name: 'D minor' },
  '7B': { key: 5, mode: 1, name: 'F major' },
  '8A': { key: 9, mode: 0, name: 'A minor' },
  '8B': { key: 0, mode: 1, name: 'C major' },
  '9A': { key: 4, mode: 0, name: 'E minor' },
  '9B': { key: 7, mode: 1, name: 'G major' },
  '10A': { key: 11, mode: 0, name: 'B minor' },
  '10B': { key: 2, mode: 1, name: 'D major' },
  '11A': { key: 6, mode: 0, name: 'F# minor' },
  '11B': { key: 9, mode: 1, name: 'A major' },
  '12A': { key: 1, mode: 0, name: 'C# minor' },
  '12B': { key: 4, mode: 1, name: 'E major' },
};

const BPM_PRESETS = [
  { name: 'Deep House', min: 118, max: 125, icon: '🌊' },
  { name: 'Tech House', min: 124, max: 130, icon: '🔧' },
  { name: 'House', min: 120, max: 128, icon: '🏠' },
  { name: 'Techno', min: 130, max: 140, icon: '⚡' },
  { name: 'Trance', min: 138, max: 145, icon: '🌀' },
  { name: 'Dubstep', min: 140, max: 150, icon: '💥' },
  { name: 'D&B', min: 170, max: 180, icon: '🥁' },
  { name: 'Downtempo', min: 90, max: 110, icon: '🌅' },
  { name: 'Ambient', min: 60, max: 90, icon: '🌙' }
];

const SpotifyAdvancedFilters = ({ 
  filters, 
  onFilterChange, 
  onApplyFilters,
  isLoading = false 
}) => {
  const [activeSection, setActiveSection] = useState('bpm');
  
  const {
    bpmEnabled = false,
    bpmRange = { min: 120, max: 130 },
    keyEnabled = false,
    selectedKeys = [],
    energyEnabled = false,
    energyRange = { min: 0.4, max: 0.8 },
    danceabilityEnabled = false,
    danceabilityRange = { min: 0.5, max: 0.9 }
  } = filters;

  const handleBpmChange = (field, value) => {
    onFilterChange({
      ...filters,
      bpmRange: { ...bpmRange, [field]: parseInt(value) || 60 }
    });
  };

  const handlePresetSelect = (preset) => {
    onFilterChange({
      ...filters,
      bpmEnabled: true,
      bpmRange: { min: preset.min, max: preset.max }
    });
  };

  const handleKeyToggle = (camelotKey) => {
    const newKeys = selectedKeys.includes(camelotKey)
      ? selectedKeys.filter(k => k !== camelotKey)
      : [...selectedKeys, camelotKey];
    
    onFilterChange({
      ...filters,
      keyEnabled: newKeys.length > 0,
      selectedKeys: newKeys
    });
  };

  const handleSliderChange = (filterType, field, value) => {
    const rangeKey = `${filterType}Range`;
    onFilterChange({
      ...filters,
      [rangeKey]: { ...filters[rangeKey], [field]: parseFloat(value) }
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (bpmEnabled) count++;
    if (keyEnabled && selectedKeys.length > 0) count++;
    if (energyEnabled) count++;
    if (danceabilityEnabled) count++;
    return count;
  };

  return (
    <div className="spotify-advanced-filters">
      <div className="saf-header">
        <div className="saf-title">
          <span className="spotify-icon">🎵</span>
          <span>Spotify Audio Filters</span>
          {getActiveFiltersCount() > 0 && (
            <span className="active-filters-badge">{getActiveFiltersCount()}</span>
          )}
        </div>
        <p className="saf-subtitle">Filter tracks by audio characteristics</p>
      </div>

      {/* Filter Section Tabs */}
      <div className="saf-tabs">
        <button 
          className={`saf-tab ${activeSection === 'bpm' ? 'active' : ''} ${bpmEnabled ? 'has-filter' : ''}`}
          onClick={() => setActiveSection('bpm')}
        >
          🎚️ BPM
        </button>
        <button 
          className={`saf-tab ${activeSection === 'key' ? 'active' : ''} ${keyEnabled ? 'has-filter' : ''}`}
          onClick={() => setActiveSection('key')}
        >
          🎹 Key
        </button>
        <button 
          className={`saf-tab ${activeSection === 'energy' ? 'active' : ''} ${energyEnabled ? 'has-filter' : ''}`}
          onClick={() => setActiveSection('energy')}
        >
          ⚡ Energy
        </button>
        <button 
          className={`saf-tab ${activeSection === 'danceability' ? 'active' : ''} ${danceabilityEnabled ? 'has-filter' : ''}`}
          onClick={() => setActiveSection('danceability')}
        >
          💃 Dance
        </button>
      </div>

      {/* BPM Section */}
      {activeSection === 'bpm' && (
        <div className="saf-section">
          <div className="saf-section-header">
            <label className="saf-toggle">
              <input
                type="checkbox"
                checked={bpmEnabled}
                onChange={(e) => onFilterChange({ ...filters, bpmEnabled: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="section-label">Filter by BPM</span>
            {bpmEnabled && (
              <span className="current-value">{bpmRange.min} - {bpmRange.max}</span>
            )}
          </div>

          {bpmEnabled && (
            <div className="saf-section-content">
              <div className="bpm-inputs">
                <div className="bpm-input-group">
                  <label>Min</label>
                  <input
                    type="number"
                    min="60"
                    max="200"
                    value={bpmRange.min}
                    onChange={(e) => handleBpmChange('min', e.target.value)}
                  />
                </div>
                <div className="bpm-slider-container">
                  <input
                    type="range"
                    min="60"
                    max="200"
                    value={bpmRange.min}
                    onChange={(e) => handleBpmChange('min', e.target.value)}
                    className="bpm-slider min-slider"
                  />
                  <input
                    type="range"
                    min="60"
                    max="200"
                    value={bpmRange.max}
                    onChange={(e) => handleBpmChange('max', e.target.value)}
                    className="bpm-slider max-slider"
                  />
                </div>
                <div className="bpm-input-group">
                  <label>Max</label>
                  <input
                    type="number"
                    min="60"
                    max="200"
                    value={bpmRange.max}
                    onChange={(e) => handleBpmChange('max', e.target.value)}
                  />
                </div>
              </div>

              <div className="bpm-presets">
                <span className="presets-label">Genre Presets:</span>
                <div className="presets-grid">
                  {BPM_PRESETS.map(preset => (
                    <button
                      key={preset.name}
                      className={`preset-btn ${bpmRange.min === preset.min && bpmRange.max === preset.max ? 'active' : ''}`}
                      onClick={() => handlePresetSelect(preset)}
                    >
                      <span className="preset-icon">{preset.icon}</span>
                      <span className="preset-name">{preset.name}</span>
                      <span className="preset-range">{preset.min}-{preset.max}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Key Section */}
      {activeSection === 'key' && (
        <div className="saf-section">
          <div className="saf-section-header">
            <label className="saf-toggle">
              <input
                type="checkbox"
                checked={keyEnabled}
                onChange={(e) => onFilterChange({ ...filters, keyEnabled: e.target.checked, selectedKeys: e.target.checked ? selectedKeys : [] })}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="section-label">Filter by Key</span>
            {keyEnabled && selectedKeys.length > 0 && (
              <span className="current-value">{selectedKeys.length} selected</span>
            )}
          </div>

          {keyEnabled && (
            <div className="saf-section-content">
              <p className="key-help">Select one or more keys (Camelot notation)</p>
              <div className="camelot-grid">
                {/* Minor Keys (A) */}
                <div className="key-row">
                  <span className="row-label">Minor (A)</span>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => {
                    const camelotKey = `${num}A`;
                    const keyInfo = CAMELOT_TO_SPOTIFY[camelotKey];
                    return (
                      <button
                        key={camelotKey}
                        className={`key-btn minor ${selectedKeys.includes(camelotKey) ? 'selected' : ''}`}
                        onClick={() => handleKeyToggle(camelotKey)}
                        title={keyInfo.name}
                      >
                        {camelotKey}
                      </button>
                    );
                  })}
                </div>
                {/* Major Keys (B) */}
                <div className="key-row">
                  <span className="row-label">Major (B)</span>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => {
                    const camelotKey = `${num}B`;
                    const keyInfo = CAMELOT_TO_SPOTIFY[camelotKey];
                    return (
                      <button
                        key={camelotKey}
                        className={`key-btn major ${selectedKeys.includes(camelotKey) ? 'selected' : ''}`}
                        onClick={() => handleKeyToggle(camelotKey)}
                        title={keyInfo.name}
                      >
                        {camelotKey}
                      </button>
                    );
                  })}
                </div>
              </div>
              {selectedKeys.length > 0 && (
                <button 
                  className="clear-keys-btn"
                  onClick={() => onFilterChange({ ...filters, selectedKeys: [], keyEnabled: false })}
                >
                  Clear All Keys
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Energy Section */}
      {activeSection === 'energy' && (
        <div className="saf-section">
          <div className="saf-section-header">
            <label className="saf-toggle">
              <input
                type="checkbox"
                checked={energyEnabled}
                onChange={(e) => onFilterChange({ ...filters, energyEnabled: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="section-label">Filter by Energy</span>
            {energyEnabled && (
              <span className="current-value">{Math.round(energyRange.min * 100)}% - {Math.round(energyRange.max * 100)}%</span>
            )}
          </div>

          {energyEnabled && (
            <div className="saf-section-content">
              <p className="slider-help">Energy measures intensity and activity. High energy = fast, loud, noisy</p>
              <div className="range-slider-group">
                <span className="range-label">Low 🌙</span>
                <div className="dual-range">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={energyRange.min}
                    onChange={(e) => handleSliderChange('energy', 'min', e.target.value)}
                  />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={energyRange.max}
                    onChange={(e) => handleSliderChange('energy', 'max', e.target.value)}
                  />
                </div>
                <span className="range-label">High ⚡</span>
              </div>
              <div className="energy-presets">
                <button onClick={() => onFilterChange({ ...filters, energyEnabled: true, energyRange: { min: 0, max: 0.4 } })}>
                  Chill
                </button>
                <button onClick={() => onFilterChange({ ...filters, energyEnabled: true, energyRange: { min: 0.4, max: 0.7 } })}>
                  Moderate
                </button>
                <button onClick={() => onFilterChange({ ...filters, energyEnabled: true, energyRange: { min: 0.7, max: 1 } })}>
                  High Energy
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Danceability Section */}
      {activeSection === 'danceability' && (
        <div className="saf-section">
          <div className="saf-section-header">
            <label className="saf-toggle">
              <input
                type="checkbox"
                checked={danceabilityEnabled}
                onChange={(e) => onFilterChange({ ...filters, danceabilityEnabled: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="section-label">Filter by Danceability</span>
            {danceabilityEnabled && (
              <span className="current-value">{Math.round(danceabilityRange.min * 100)}% - {Math.round(danceabilityRange.max * 100)}%</span>
            )}
          </div>

          {danceabilityEnabled && (
            <div className="saf-section-content">
              <p className="slider-help">How suitable a track is for dancing based on tempo, rhythm, and beat</p>
              <div className="range-slider-group">
                <span className="range-label">Less 🚶</span>
                <div className="dual-range">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={danceabilityRange.min}
                    onChange={(e) => handleSliderChange('danceability', 'min', e.target.value)}
                  />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={danceabilityRange.max}
                    onChange={(e) => handleSliderChange('danceability', 'max', e.target.value)}
                  />
                </div>
                <span className="range-label">More 💃</span>
              </div>
              <div className="energy-presets">
                <button onClick={() => onFilterChange({ ...filters, danceabilityEnabled: true, danceabilityRange: { min: 0, max: 0.4 } })}>
                  Listening
                </button>
                <button onClick={() => onFilterChange({ ...filters, danceabilityEnabled: true, danceabilityRange: { min: 0.5, max: 0.75 } })}>
                  Groovy
                </button>
                <button onClick={() => onFilterChange({ ...filters, danceabilityEnabled: true, danceabilityRange: { min: 0.75, max: 1 } })}>
                  Dance Floor
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Apply Button */}
      <div className="saf-actions">
        <button 
          className="apply-filters-btn"
          onClick={onApplyFilters}
          disabled={isLoading || getActiveFiltersCount() === 0}
        >
          {isLoading ? (
            <>🔄 Searching...</>
          ) : (
            <>🔍 Apply Filters {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}</>
          )}
        </button>
        <button 
          className="clear-all-btn"
          onClick={() => onFilterChange({
            bpmEnabled: false,
            bpmRange: { min: 120, max: 130 },
            keyEnabled: false,
            selectedKeys: [],
            energyEnabled: false,
            energyRange: { min: 0.4, max: 0.8 },
            danceabilityEnabled: false,
            danceabilityRange: { min: 0.5, max: 0.9 }
          })}
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

export default SpotifyAdvancedFilters;
